import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShipToEditor, EMPTY_SHIP } from "./ShipToEditor";

const EMPTY = {
  name: "", customer_type: "Regular", contact_person: "", mobile: "", email: "",
  pan: "", gstin: "", address: "", city: "", state: "", pin: "", place_of_supply: "",
  payment_terms: "", credit_limit: "", credit_days: "", sales_person: "", remarks: "",
};
export default function CustomerForm({ open, onOpenChange, customer, onSaved }) {
  const { user } = useAuth();
  const editing = !!customer;
  const [f, setF] = useState(EMPTY);
  const [ships, setShips] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (customer) {
      const b = customer.billing || {};
      setF({
        name: customer.name || "", customer_type: customer.customer_type || "Regular",
        contact_person: b.contact || "", mobile: b.mobile || "", email: b.email || "",
        pan: b.pan || "", gstin: b.gstin || "", address: b.address || "", city: b.city || "",
        state: b.state || customer.state || "", pin: b.pin || "",
        place_of_supply: customer.place_of_supply || "", payment_terms: customer.payment_terms || "",
        credit_limit: customer.credit_limit ?? "", credit_days: customer.credit_days ?? "",
        sales_person: customer.sales_person || "", remarks: customer.remarks || "",
      });
      setShips((customer.ship_tos || []).map((s) => ({ ...EMPTY_SHIP, ...s, _k: crypto.randomUUID() })));
    } else {
      setF(EMPTY);
      setShips([]);
    }
  }, [open, customer]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (allowDup = false) => {
    if (!f.name.trim()) return toast.error("Customer Name is required");
    setBusy(true);
    const payload = {
      ...f,
      credit_limit: parseFloat(f.credit_limit) || 0,
      credit_days: parseInt(f.credit_days) || 0,
      ship_tos: ships.filter((s) => s.name || s.address).map(({ _k, ...rest }) => rest),
      allow_duplicate_gstin: allowDup,
    };
    try {
      const res = editing
        ? await api.put(`/customers/${customer.code}`, payload)
        : await api.post("/customers", payload);
      toast.success(editing ? "Customer updated" : `Customer ${res.data.code} created`);
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && user?.role === "admin" && !allowDup) {
        if (window.confirm(`${d}\n\nOverride and save anyway?`)) {
          setBusy(false);
          return submit(true);
        }
        toast.error(typeof d === "string" ? d : "Duplicate GSTIN — customer not saved");
      } else {
        toast.error(typeof d === "string" ? d : "Failed to save customer");
      }
    } finally {
      setBusy(false);
    }
  };

  const F = (label, key, props = {}) => (
    <div><Label className="text-xs">{label}</Label>
      <Input data-testid={`cf-${key}`} value={f[key]} onChange={(e) => set(key, e.target.value)} {...props} /></div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="customer-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase">
            {editing ? `Edit Customer — ${customer.code}` : "Add New Customer"}
          </DialogTitle>
          <DialogDescription className="sr-only">Customer master form</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {editing && <p className="text-xs font-mono text-muted-foreground">Customer Code: <span className="metric text-primary">{customer.code}</span></p>}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {F("Customer Name *", "name")}
            <div><Label className="text-xs">Customer Type</Label>
              <Select value={f.customer_type} onValueChange={(v) => set("customer_type", v)}>
                <SelectTrigger data-testid="cf-customer-type"><SelectValue /></SelectTrigger>
                <SelectContent>{["Regular", "Retail", "Distributor", "Export", "One-time"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            {F("Contact Person", "contact_person")}
            {F("Mobile", "mobile")}
            {F("Email", "email", { type: "email" })}
            {F("PAN", "pan", { placeholder: "ABCDE1234F" })}
            {F("GSTIN", "gstin", { placeholder: "33ABCDE1234F1Z5" })}
            {F("State", "state")}
            {F("Place of Supply", "place_of_supply")}
            {F("City", "city")}
            {F("PIN Code", "pin")}
            {F("Sales Person", "sales_person")}
          </div>
          <div><Label className="text-xs">Billing Address</Label>
            <Textarea data-testid="cf-address" value={f.address} onChange={(e) => set("address", e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-3">
            {F("Payment Terms", "payment_terms", { placeholder: "30 days" })}
            {F("Credit Limit ₹", "credit_limit", { type: "number" })}
            {F("Credit Days", "credit_days", { type: "number" })}
          </div>
          <div><Label className="text-xs">Remarks</Label>
            <Input data-testid="cf-remarks" value={f.remarks} onChange={(e) => set("remarks", e.target.value)} /></div>

          <ShipToEditor ships={ships} setShips={setShips} />

          <Button className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-bold" disabled={busy} onClick={() => submit(false)} data-testid="cf-save">
            {editing ? "SAVE CHANGES" : "SAVE CUSTOMER"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
